import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Lock, Mail, AlertCircle, LogIn } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatError(detail) {
  if (!detail) return "Une erreur s'est produite";
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(' ');
  if (detail?.message) return detail.message;
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || '/mon-compte';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [needVerify, setNeedVerify] = useState(false);
  const [resending, setResending] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNeedVerify(false);
    setSubmitting(true);
    try {
      const data = await login(email, password);
      toast.success('Connexion réussie');
      navigate(data.role === 'admin' ? '/admin' : redirectTo);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'email_not_verified') {
        setNeedVerify(true);
        setError('');
      } else {
        setError(formatError(detail) || err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      toast.success('Email de confirmation renvoyé');
    } catch {
      toast.error("Impossible d'envoyer l'email");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-[80vh] bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-lg p-8 border border-gray-100">
          <div className="text-center mb-6">
            <div className="inline-flex w-12 h-12 bg-[#E42313]/10 rounded-full items-center justify-center mb-3">
              <LogIn className="w-6 h-6 text-[#E42313]" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Connexion</h1>
            <p className="text-gray-500 text-sm mt-1">Accédez à votre espace client</p>
          </div>

          {error && (
            <div data-testid="login-error" className="mb-5 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {needVerify && (
            <div data-testid="login-verify-needed" className="mb-5 bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded text-sm">
              <p className="font-semibold mb-2">Confirmez votre adresse email</p>
              <p className="mb-3">
                Nous vous avons envoyé un email de confirmation à <strong>{email}</strong>. Cliquez sur le lien
                pour activer votre compte.
              </p>
              <button
                onClick={resend}
                disabled={resending}
                className="text-sm underline text-amber-900 hover:text-amber-700 disabled:opacity-50"
                data-testid="login-resend-verification"
              >
                {resending ? 'Envoi…' : "Renvoyer l'email"}
              </button>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#E42313]"
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between items-baseline mb-1">
                <label className="block text-sm font-medium text-gray-700">Mot de passe</label>
                <Link to="/mot-de-passe-oublie" data-testid="login-forgot-link" className="text-xs text-[#E42313] hover:underline">
                  Oublié&nbsp;?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  data-testid="login-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#E42313]"
                />
              </div>
            </div>
            <button
              data-testid="login-submit"
              type="submit"
              disabled={submitting}
              className="w-full bg-[#E42313] text-white py-3 rounded font-semibold hover:bg-[#C11A0F] transition-colors disabled:opacity-50"
            >
              {submitting ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>

          <p className="text-center text-sm text-gray-600 mt-6">
            Pas encore de compte ?{' '}
            <Link
              to="/inscription"
              state={{ from: redirectTo }}
              className="text-[#E42313] font-semibold hover:underline"
              data-testid="login-register-link"
            >
              Créer un compte
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
